// Canonical helper for deciding whether two text strings denote the same
// legally binding instrument. Consumed by the Customer module classifier
// (CustomerForm.js) and by the Legal Register module's AI recommendations
// dedup step (LegalRegisterView.js). Any future module that compares
// legal-instrument titles must reuse areEquivalent from this file instead of
// inlining its own matching logic.
//
// Matching is two-tier. First, both inputs are normalized via
// normalizeForMatch (lowercase, NFD-decompose, strip combining marks, strip a
// fixed punctuation set, collapse whitespace) and compared with
// contains-bidirectional — this handles trivial punctuation and whitespace
// drift. If that check fails, areEquivalent iterates LEGAL_EQUIVALENT_GROUPS
// and reports true when both inputs contain at least one variant from the
// same group — this handles semantic equivalence between e.g. "GDPR" and
// "Reglamento General de Protección de Datos".
//
// Extending LEGAL_EQUIVALENT_GROUPS: each group is a flat array of strings
// all of which denote the same legally binding instrument. Order inside a
// group is irrelevant. Groups are organised by jurisdiction under section
// comments. To add a new variant to a known instrument, append a string to
// the existing group. To add a new instrument, append a new flat array
// under the appropriate jurisdiction section. The table is for legally
// binding instruments only; voluntary standards and frameworks are
// intentionally excluded, as is process safety, environmental, labor, tax
// and commercial law — those domains are out of the ISMS scope of this
// tool.

export const LEGAL_EQUIVALENT_GROUPS = [
  // ── European Union ──────────────────────────────────────────────────────
  ['DORA', 'Digital Operational Resilience Act', 'Regulation (EU) 2022/2554', 'Reglamento (UE) 2022/2554'],
  ['eIDAS', 'eIDAS Regulation', 'Regulation (EU) 910/2014', 'Regulation (EU) 2024/1183', 'Reglamento (UE) 910/2014'],
  ['ePrivacy Directive', 'Directive 2002/58/EC', 'Directiva 2002/58/CE'],
  ['EU AI Act', 'Artificial Intelligence Act', 'Regulation (EU) 2024/1689', 'Reglamento (UE) 2024/1689'],
  ['EU Cyber Resilience Act', 'EU CRA', 'Cyber Resilience Act', 'Regulation (EU) 2024/2847', 'Reglamento (UE) 2024/2847'],
  ['GDPR', 'General Data Protection Regulation', 'Reglamento General de Protección de Datos', 'Regulation (EU) 2016/679', 'Reglamento (UE) 2016/679'],
  ['NIS2', 'NIS 2', 'NIS2 Directive', 'Directive (EU) 2022/2555', 'Directiva (UE) 2022/2555'],

  // ── Spain ───────────────────────────────────────────────────────────────
  ['ENS', 'Esquema Nacional de Seguridad', 'Real Decreto 311/2022'],
  ['LOPDGDD', 'Ley Orgánica 3/2018', 'Ley Orgánica de Protección de Datos Personales y Garantía de los Derechos Digitales'],
  ['LSSI', 'LSSI-CE', 'Ley 34/2002 de Servicios de la Sociedad de la Información'],

  // ── Germany ─────────────────────────────────────────────────────────────
  ['BDSG', 'Bundesdatenschutzgesetz', 'Federal Data Protection Act'],
  ['TTDSG', 'Telekommunikation-Telemedien-Datenschutz-Gesetz'],

  // ── United States — federal ─────────────────────────────────────────────
  ['CCPA', 'California Consumer Privacy Act'],
  ['CIRCIA', 'Cyber Incident Reporting for Critical Infrastructure Act'],
  ['CPRA', 'California Privacy Rights Act'],
  ['FERPA', 'Family Educational Rights and Privacy Act'],
  ['FTC Act §5', 'FTC Act Section 5', 'Federal Trade Commission Act Section 5', 'Section 5 of the FTC Act'],
  ['GLBA', 'Gramm-Leach-Bliley Act'],
  ['HIPAA', 'Health Insurance Portability and Accountability Act'],
  ['SEC Item 1.05', 'SEC Cybersecurity Disclosure Rule', 'Item 1.05 of Form 8-K'],

  // ── United States — states ──────────────────────────────────────────────
  ['NY SHIELD Act', 'Stop Hacks and Improve Electronic Data Security Act'],
  ['Texas BC §521', 'Texas Business & Commerce Code §521', 'Identity Theft Enforcement and Protection Act'],
  ['Texas SB-820', 'Texas Senate Bill 820'],

  // ── Latin America ───────────────────────────────────────────────────────
  ['Argentina Ley 25.326', 'Ley de Protección de Datos Personales Argentina'],
  ['Chile Ley 19.628', 'Ley sobre Protección de la Vida Privada'],
  ['Colombia Ley 1581/2012', 'Ley Estatutaria 1581 de 2012'],
  ['LFPDPPP', 'Ley Federal de Protección de Datos Personales en Posesión de los Particulares'],
  ['LGPD', 'Lei Geral de Proteção de Dados Pessoais', 'Ley 13.709/2018'],

  // ── Asia-Pacific ────────────────────────────────────────────────────────
  ['APPI', 'Act on the Protection of Personal Information'],
  ['Australia Privacy Act 1988', 'Privacy Act 1988'],
  ['China CSL', 'China Cybersecurity Law', 'PRC Cybersecurity Law'],
  ['China DSL', 'Data Security Law of the People\'s Republic of China'],
  ['China PIPL', 'Personal Information Protection Law'],
  ['Singapore PDPA', 'Personal Data Protection Act 2012'],

  // ── International contractual ───────────────────────────────────────────
  ['PCI DSS', 'Payment Card Industry Data Security Standard'],
];

const PUNCT_TO_SPACE = /[()[\]{}.,;:!?'"`–—\-/\\]/g;

export function normalizeForMatch(s) {
  if (typeof s !== 'string' || s === '') return '';
  const out = s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(PUNCT_TO_SPACE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return out;
}

export function areEquivalent(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  const na = normalizeForMatch(a);
  const nb = normalizeForMatch(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  for (const group of LEGAL_EQUIVALENT_GROUPS) {
    let aHit = false;
    let bHit = false;
    for (const variant of group) {
      const nv = normalizeForMatch(variant);
      if (!nv) continue;
      if (!aHit && na.includes(nv)) aHit = true;
      if (!bHit && nb.includes(nv)) bHit = true;
      if (aHit && bHit) return true;
    }
  }
  return false;
}
